<template>
  <v-card class="mb-3 rounded-lg" outlined>
    <div class="pa-4">
      <!-- Header -->
      <div class="d-flex flex-column flex-md-row justify-space-between align-md-center mb-3">
        <div class="d-flex align-center mb-2 mb-md-0">
          <v-chip :color="actionColor" dark small class="mr-3 font-weight-bold">
            {{ actionLabel }}
          </v-chip>
          <v-chip outlined small color="primary" class="mr-3">
            {{ collectionLabel }}
          </v-chip>
        </div>
        <div class="d-flex align-center grey--text text-body-2" style="gap: 12px">
          <span>
            <v-icon x-small class="mr-1">mdi-account</v-icon>{{ log.userId }}
          </span>
          <span>
            <v-icon x-small class="mr-1">mdi-clock-outline</v-icon>{{ formattedTime }}
          </span>
          <span class="d-none d-md-inline">
            <v-icon x-small class="mr-1">mdi-ip-network</v-icon>{{ log.ip }}
          </span>
        </div>
      </div>

      <!-- Cambios detectados -->
      <div v-if="log.changes && Object.keys(log.changes).length > 0">
        <p class="text-body-2 font-weight-bold mb-2 primary--text">Cambios:</p>
        <div
          v-for="(change, field) in log.changes"
          :key="field"
          class="d-flex align-center mb-1 ml-2"
        >
          <span class="text-body-2 font-weight-bold mr-2" style="min-width: 100px">{{ formatField(field) }}:</span>
          <v-chip x-small color="error" dark class="mr-1">{{ formatValue(change.from) }}</v-chip>
          <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
          <v-chip x-small color="success" dark>{{ formatValue(change.to) }}</v-chip>
        </div>
      </div>

      <!-- CREATE: mostrar datos nuevos -->
      <div v-else-if="log.action === 'CREATE' && log.newData">
        <p class="text-body-2 font-weight-bold mb-2 primary--text">Datos creados:</p>
        <div class="d-flex flex-wrap ml-2" style="gap: 8px">
          <div
            v-for="(value, field) in log.newData"
            :key="field"
            class="text-body-2"
          >
            <span class="font-weight-bold">{{ formatField(field) }}:</span>
            <span class="ml-1">{{ formatValue(value) }}</span>
          </div>
        </div>
      </div>

      <!-- UPDATE sin cambios detectados pero con datos -->
      <div v-else-if="log.action === 'UPDATE' && log.newData">
        <p class="text-body-2 font-weight-bold mb-2 grey--text">Sin cambios detectados</p>
        <div class="d-flex flex-wrap ml-2" style="gap: 8px">
          <div
            v-for="(value, field) in log.newData"
            :key="field"
            class="text-body-2"
          >
            <span class="font-weight-bold">{{ formatField(field) }}:</span>
            <span class="ml-1">{{ formatValue(value) }}</span>
          </div>
        </div>
      </div>

      <!-- Expandir detalles completos -->
      <v-expansion-panels flat class="mt-2" v-if="log.previousData || log.newData">
        <v-expansion-panel>
          <v-expansion-panel-header class="text-body-2 pa-0">
            <span class="grey--text">Ver datos completos</span>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col cols="12" md="6" v-if="log.previousData">
                <p class="text-body-2 font-weight-bold error--text mb-2">Datos anteriores</p>
                <div
                  v-for="(value, field) in log.previousData"
                  :key="'prev-' + field"
                  class="text-body-2 mb-1"
                >
                  <span class="font-weight-bold">{{ formatField(field) }}:</span>
                  <span class="ml-1">{{ formatValue(value) }}</span>
                </div>
              </v-col>
              <v-col cols="12" md="6" v-if="log.newData">
                <p class="text-body-2 font-weight-bold success--text mb-2">Datos nuevos</p>
                <div
                  v-for="(value, field) in log.newData"
                  :key="'new-' + field"
                  class="text-body-2 mb-1"
                >
                  <span class="font-weight-bold">{{ formatField(field) }}:</span>
                  <span class="ml-1">{{ formatValue(value) }}</span>
                </div>
              </v-col>
            </v-row>
            <div class="mt-2 text-body-2 grey--text">
              <span class="mr-4"><strong>Document ID:</strong> {{ log.documentId }}</span>
              <span class="d-inline d-md-none"><strong>IP:</strong> {{ log.ip }}</span>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </v-card>
</template>

<script>
import { FormatsMixin } from "@/mixins/Formats";

export default {
  name: "AuditLogItem",
  mixins: [FormatsMixin],
  props: {
    log: {
      type: Object,
      required: true,
    },
  },
  computed: {
    actionColor() {
      const colors = {
        CREATE: "success",
        UPDATE: "warning darken-1",
        DELETE: "error",
      };
      return colors[this.log.action] || "grey";
    },
    actionLabel() {
      const labels = {
        CREATE: "CREADO",
        UPDATE: "ACTUALIZADO",
        DELETE: "ELIMINADO",
      };
      return labels[this.log.action] || this.log.action;
    },
    collectionLabel() {
      const map = {
        plan_homes: "Pagos Hogar",
        receipts: "Recibos",
        clients: "Clientes",
        temporal_clients: "Clientes Temporales",
        antennas: "Antenas",
        services: "Servicios",
        users: "Usuarios",
      };
      return map[this.log.collectionName] || this.log.collectionName;
    },
    formattedTime() {
      if (!this.log.timestamp) return "";
      const d = new Date(this.log.timestamp);
      return d.toLocaleTimeString("es-MX", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    },
  },
  methods: {
    formatField(field) {
      const map = {
        mount: "Monto",
        start: "Inicio",
        end: "Fin",
        clientName: "Cliente",
        idClient: "ID Cliente",
        concepto: "Concepto",
        total: "Total",
        username: "Usuario",
        rol: "Rol",
        estatus: "Estatus",
        name: "Nombre",
        price: "Precio",
        description: "Descripción",
      };
      return map[field] || field;
    },
    formatValue(value) {
      if (value === null || value === undefined) return "—";
      if (typeof value === "boolean") return value ? "Activo" : "Inactivo";
      if (typeof value === "string" && value.match(/^\d{4}-\d{2}-\d{2}T/)) {
        return this.getShortDate(value);
      }
      if (typeof value === "number") {
        return value.toLocaleString("es-MX");
      }
      return String(value);
    },
  },
};
</script>
